// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import { Portal } from 'react-portal';
import cx from 'classnames';
import Tree from './tree';
import { getScrollParents } from '../utils/dom';
import css from './css';

// This is a component that renders its children into a portal and automatically manages
// the maxWidth and maxHeight of the children container. Its main use case currently is
// for dropdown menus. To have dropdown menus work inside any container, the menu really
// needs to be rendered directly inside the <body> so it's never clipped by scroll containers.
// The 2nd goal is to listen for changes in the viewport so that we can modify the dimensions
// of the overlay. i.e. when a dropdown opens with many options, fill the available
// viewport space underneath the field, but don't let the menu go offscreen.
export default class Tether extends React.Component {
  static propTypes = {
    children: PropTypes.any.isRequired,
    onPositionChange: PropTypes.func,
    show: PropTypes.bool.isRequired,
    viewportPaddingTop: PropTypes.number,
    viewportPaddingLeft: PropTypes.number,
    viewportPaddingRight: PropTypes.number,
    viewportPaddingBottom: PropTypes.number,
    desiredHeight: PropTypes.any,
    desiredWidth: PropTypes.any,
    horizontalAlignment: PropTypes.oneOf(['center', 'left', 'right']),
    horizontalOffset: PropTypes.number,
    verticalOffset: PropTypes.number,
    allowAbove: PropTypes.bool,
  };

  static defaultProps = {
    viewportPaddingTop: 16,
    viewportPaddingLeft: 16,
    viewportPaddingRight: 16,
    viewportPaddingBottom: 16,
    horizontalOffset: 0,
    verticalOffset: 0,
    horizontalAlignment: 'left',
    allowAbove: true,
  };

  // We have to manually keep track of a tree of nodes that represent the hierarchy of the tethers.
  // Since all of the overlays are direct descendants of the <body>, we still need to be able determine
  // if one tether is contained within another. This is what allows popovers to open popovers, like a dropdown
  // menu inside a modal.
  static tree = new Tree();
  static tethers = [];

  static containsDOMNode(domNode) {
    return (node) => node.tether && node.tether.containerDOMNode && node.tether.containerDOMNode.contains(domNode);
  }

  static byTether(tether) {
    return (node) => node.tether === tether;
  }

  // remove a tethered component from the global tree
  static removeNode(tether) {
    this.tethers = this.tethers.filter((t) => t !== tether);
    this.tree.removeNode(this.containsDOMNode(tether.targetDOMNode));
  }

  // insert a new tether into the tree at the correct location based on containment
  static insertNode(tether) {
    this.removeNode(tether);
    this.tethers.push(tether);
    this.tree.insertNode(this.containsDOMNode(tether.targetDOMNode), { tether });
  }

  static positionAll() {
    for (const tether of this.tethers) {
      tether.position();
    }
  }

  static tetherContainsDOMNode(tether, domNode) {
    // find the tree node for the given tether
    const node = this.tree.find(null, this.byTether(tether));

    // Find the tether node that contains the given DOM node starting at the current tether
    // This returns a non-null value when called with a DOM node that's a child node of a
    // child tether. For example, checking if a select option is a child of a popover that contains
    // the select.
    const descendantTether = this.tree.find(node, this.containsDOMNode(domNode));

    return descendantTether != null;
  }

  constructor(props) {
    super(props);

    this.state = {
      minWidth: 0,
      maxWidth: Number.MAX_SAFE_INTEGER,
      minHeight: 0,
      maxHeight: Number.MAX_SAFE_INTEGER,
    };
  }

  componentDidMount() {
    Tether.insertNode(this);

    this.addEvents();

    this.position();
  }

  componentWillUnmount() {
    Tether.removeNode(this);

    this.removeEvents();
  }

  render() {
    const { styles } = Tether;

    if (!this.props.show) {
      return null;
    }

    return (
      <div ref={this.bindTarget}>
        <Portal
          isOpened
          ref={this.bindPortal}
        >
          <div
            className={cx('tether', styles.container)}
            ref={this.bindContainer}
          >
            {this.renderChildren()}
          </div>
        </Portal>
      </div>
    );
  }

  renderChildren() {
    if (typeof this.props.children === 'function') {
      return this.props.children(this.state);
    }
    return this.props.children;
  }

  get targetDOMNode() {
    return this._target;
  }

  get containerDOMNode() {
    return this._container;
  }

  bindPortal = (node) => {
    this._portal = node;
  };

  bindTarget = (node) => {
    this._target = node;
    this._scrollParents = node ? getScrollParents(node) : null;
  };

  bindContainer = (node) => {
    this._container = node;

    this.position();
  };

  addEvents() {
    this.removeEvents();

    window.addEventListener('resize', this.handlePosition, true);

    if (this._scrollParents) {
      for (const element of this._scrollParents) {
        element.addEventListener('scroll', this.handlePosition);
      }
    }
  }

  removeEvents() {
    window.removeEventListener('resize', this.handlePosition, true);

    if (this._scrollParents) {
      for (const element of this._scrollParents) {
        element.removeEventListener('scroll', this.handlePosition);
      }
    }
  }

  handlePosition = (event) => {
    this.position();
  };

  get desiredHeight() {
    if (typeof this.props.desiredHeight === 'function') {
      return this.props.desiredHeight(this);
    }

    return this.props.desiredHeight || 100;
  }

  get desiredWidth() {
    if (typeof this.props.desiredWidth === 'function') {
      return this.props.desiredWidth(this);
    }

    return this.props.desiredWidth;
  }

  position() {
    if (this._target && this._container) {
      const targetBox = this._target.getBoundingClientRect();

      const { scrollX, scrollY } = window;

      const bodyOffsetX = targetBox.left + scrollX;
      const bodyOffsetY = targetBox.top + scrollY;

      if (this._portal) {
        const node = this._portal.defaultNode;

        node.style.position = 'absolute';
        node.style.top = '0';
        node.style.left = '0';
        node.style.right = '0';
        node.style.bottom = '0';
        node.style.pointerEvents = 'none';
      }

      // leave space around the viewport
      const windowWidth = (document.documentElement.clientWidth + scrollX) - (this.props.viewportPaddingLeft + this.props.viewportPaddingRight);
      const windowHeight = (document.documentElement.clientHeight + scrollY) - (this.props.viewportPaddingTop + this.props.viewportPaddingBottom);

      const availableWidth = windowWidth - bodyOffsetX;

      const { desiredHeight } = this;
      const { desiredWidth } = this;

      let availableHeight = windowHeight - bodyOffsetY;

      let targetTop = targetBox.top + scrollY + this.props.verticalOffset;
      let targetLeft = targetBox.left + scrollX + this.props.horizontalOffset;

      if (this.props.horizontalAlignment === 'center') {
        targetLeft -= (desiredWidth / 2);
      } else if (this.props.horizontalAlignment === 'right') {
        targetLeft -= desiredWidth;
      }

      if (this.props.allowAbove && availableHeight < desiredHeight) {
        // There isn't enough room under the target to fit the menu, move it above the target
        //
        // negate the verticalOffset (if asked to move down 20, move it up 20)
        targetTop = targetBox.top - this.props.verticalOffset;

        availableHeight = targetTop;

        let top = targetTop - desiredHeight - 30;

        // if the menu is offscreen above, move it back into the viewport and adjust the height to fit
        if (top < 0) {
          top = Math.max(window.scrollY + this.props.viewportPaddingTop, windowHeight + window.scrollY - desiredHeight);

          availableHeight = Math.max(desiredHeight, windowHeight);
        }

        this._container.style.top = `${top + scrollY}px`;
        this._container.style.left = `${targetLeft}px`;
      } else {
        this._container.style.top = `${targetTop}px`;
        this._container.style.left = `${targetLeft}px`;
      }

      // cap the height to viewport
      availableHeight = Math.min(availableHeight, windowHeight);

      const minWidth = this._target.previousSibling.offsetWidth;

      this.setState({ minWidth, maxWidth: availableWidth, maxHeight: availableHeight });

      this.props.onPositionChange({ minWidth, maxWidth: availableWidth, maxHeight: availableHeight });
    }
  }

  containsDOMNode(domNode) {
    return Tether.tetherContainsDOMNode(this, domNode);
  }
}

const styles = css({
  container: {
    position: 'absolute',
    zIndex: 99999,
    pointerEvents: 'all',
  },
});

Tether.styles = styles;
